import type { PrintTemplate, PrintTemplateSection } from "@repo/types";
import type { PrintDataFields } from "../hooks/use-print-data";
import type {
  PrintFieldDef,
  PrintSectionDef,
  ResolvedItem,
  ResolvedSection,
} from "./types";

export type PrintTarget = "web" | "pdf";

const isEmpty = (value: string | null | undefined): boolean =>
  value === null || value === undefined || String(value).trim() === "";

/**
 * Ajout automatique des champs du registre absents d'un modèle enregistré : ils sont ajoutés en fin de leur section
 * (avec leur visibilité par défaut), pour que les nouveaux champs apparaissent sans migration des modèles existants.
 */
export function withRegistryDefaults<TData>(
  template: PrintTemplate,
  fields: PrintFieldDef<TData>[],
): PrintTemplate {
  return {
    ...template,
    sections: template.sections.map((section): PrintTemplateSection => {
      const known = new Set(section.items.map((i) => i.field));
      const missing = fields.filter(
        (f) => f.section === section.id && !known.has(f.key),
      );
      if (missing.length === 0) return section;
      return {
        ...section,
        items: [
          ...section.items,
          ...missing.map((f) => ({
            field: f.key,
            visible: f.defaultVisible,
            col: f.defaultCol,
          })),
        ],
      };
    }),
  };
}

/**
 * Transforme un modèle + les données d'impression en une structure simple (sections → colonnes → éléments).
 * Fonction pure, sans React : les rendus HTML et PDF ne font plus que du style à partir de ce résultat.
 * Les champs ou sections inconnus sont ignorés (un ancien client doit pouvoir imprimer un modèle plus récent).
 *
 * `target` : le PDF actuel diffère un peu de la fiche web (libellé « Code site », titres en majuscules, colonne du
 * cultivar, valeur de la DICT). Le registre porte ces écarts, pour que chaque rendu garde exactement ses libellés.
 * Un libellé, un titre ou une colonne choisi dans le modèle s'applique aux deux rendus.
 */
export function resolveLayout<TData = PrintDataFields>(
  template: PrintTemplate,
  sections: PrintSectionDef[],
  fields: PrintFieldDef<TData>[],
  data: TData,
  target: PrintTarget = "web",
): ResolvedSection[] {
  const defBySection = new Map(sections.map((s) => [s.id, s]));
  const fieldByKey = new Map(fields.map((f) => [f.key, f]));
  const merged = withRegistryDefaults(template, fields);

  const resolved: ResolvedSection[] = [];
  for (const section of merged.sections) {
    const def = defBySection.get(section.id);
    if (!def || !section.visible) continue;

    const columnCount = Math.max(1, section.columns);
    const columns: ResolvedItem[][] = Array.from(
      { length: columnCount },
      () => [],
    );

    for (const item of section.items) {
      const field = fieldByKey.get(item.field);
      if (!field || !item.visible) continue;

      const raw = ((target === "pdf" && field.pdfResolve) || field.resolve)(
        data,
      );
      const hide = item.hideIfEmpty ?? field.hideIfEmpty ?? false;
      if (field.kind === "text" && hide && isEmpty(raw)) continue;

      const value = isEmpty(raw)
        ? ""
        : `${raw}${field.unit ? ` ${field.unit}` : ""}`;
      // Colonne du PDF actuel tant que le modèle garde la colonne par défaut du champ ; sinon, celle du modèle.
      const wanted =
        target === "pdf" && field.pdfCol && item.col === field.defaultCol
          ? field.pdfCol
          : item.col;
      const col = Math.min(Math.max(wanted, 1), columnCount) - 1;
      const customLabel = item.label?.trim();
      columns[col]!.push({
        key: field.key,
        kind: field.kind,
        label:
          customLabel ||
          (target === "pdf" ? field.pdfLabel : undefined) ||
          field.label,
        value,
      });
    }

    if (columns.every((c) => c.length === 0)) continue; // une section sans aucun élément visible n'est pas imprimée
    const customTitle = section.title?.trim();
    resolved.push({
      id: section.id,
      title:
        target === "pdf"
          ? customTitle
            ? customTitle.toUpperCase()
            : (def.pdfTitle ?? def.title.toUpperCase())
          : customTitle || def.title,
      style: section.style,
      plainTitle: def.plainTitle,
      columns,
    });
  }
  return resolved;
}
